import React, { useEffect, useState, useRef } from "react";
import { Box, TextField, Button, Grid, LinearProgress, Chip, Paper, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import useAuth from "../context/AuthProvider";
import { apiClient } from "../helper/api";
import Swal from "sweetalert2";
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, convertToRaw, EditorState, convertFromHTML, AtomicBlockUtils } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';

interface EmailFormData {
  cc: string[];
  bcc: string[];
  subject: string;
  emailBody: string;
  attachments: File[];
}

const emailTemplate = `
<p><strong>New Features Now Available</strong></p>

<p><strong>[Feature 1 Name]</strong><br />
[Brief explanation of what it does and how it benefits the user]<br />
[Screenshot or video link]</p>

<p><strong>[Feature 2 Name]</strong><br />
[Brief explanation of what it does and how it benefits the user]<br />
[Screenshot or video link]</p>

<p><strong>[Feature 3 Name]</strong><br />
[Brief explanation of what it does and how it benefits the user]<br />
[Screenshot or video link]</p>

<p>These enhancements are part of our ongoing commitment to making the portal more intuitive, efficient, and helpful for your daily operations.</p>

<p><strong>Updated Training Guides</strong></p>
<p>Attached are the updated Client Portal Training Guides, which now include our latest features for your reference.</p>
`;

export const SendClientPortalUpdate = () => {
  const { user } = useAuth();
  const token = user?.token;
  const [loading, setLoading] = useState(false);
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
  const [attachments, setAttachments] = useState<File[]>([]);
  const editorWrapperRef = useRef<HTMLDivElement>(null);

  const initialValues: EmailFormData = {
    cc: [],
    bcc: [],
    subject: "Exciting Updates to the MISC Client Portal!",
    emailBody: emailTemplate,
    attachments: []
  };

  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const handleAddEmail = (
    input: string,
    fieldName: "cc" | "bcc",
    currentEmails: string[],
    setFieldValue: (field: string, value: string[]) => void,
    setInput: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const email = input.trim().replace(/[,;]+$/, "");
    if (email && isValidEmail(email) && !currentEmails.includes(email)) {
      setFieldValue(fieldName, [...currentEmails, email]);
      setInput("");
    }
  };

  const handleEmailKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    input: string,
    fieldName: "cc" | "bcc",
    currentEmails: string[],
    setFieldValue: (field: string, value: string[]) => void,
    setInput: React.Dispatch<React.SetStateAction<string>>
  ) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab" || e.key === " ") {
      e.preventDefault();
      handleAddEmail(input, fieldName, currentEmails, setFieldValue, setInput);
    } else if (e.key === "Backspace" && !input && currentEmails.length > 0) {
      setFieldValue(fieldName, currentEmails.slice(0, -1));
    }
  };

  const handleRemoveEmail = (
    index: number,
    fieldName: "cc" | "bcc",
    currentEmails: string[],
    setFieldValue: (field: string, value: string[]) => void
  ) => {
    setFieldValue(fieldName, currentEmails.filter((_, i) => i !== index));
  };

  // Initialize editor with template
  useEffect(() => {
    const contentBlock = convertFromHTML(emailTemplate);
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
    const _editorState = EditorState.createWithContent(contentState);
    setEditorState(_editorState);
  }, []);

  // Handle paste events to support pasting images
  useEffect(() => {
    const editorWrapper = editorWrapperRef.current;
    if (!editorWrapper) return;

    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            
            try {
              // Convert file to base64 data URL
              const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
              });

              // Insert image into editor at cursor position
              setEditorState((currentState) => {
                const contentState = currentState.getCurrentContent();
                const contentStateWithEntity = contentState.createEntity(
                  'IMAGE',
                  'IMMUTABLE',
                  { src: dataUrl, alt: file.name || 'Pasted image' }
                );
                const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
                return AtomicBlockUtils.insertAtomicBlock(currentState, entityKey, ' ');
              });
            } catch (error) {
              console.error('Error inserting pasted image:', error);
            }
          }
        }
      }
    };

    editorWrapper.addEventListener('paste', handlePaste);
    return () => {
      editorWrapper.removeEventListener('paste', handlePaste);
    };
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values: EmailFormData, { resetForm }: any) => {
    // Convert editor content to HTML
    const emailBodyHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    // Prepare FormData for API call
    const formData = new FormData();
    
    // Add email data - backend will handle fetching Client Admin users and replacing [Client Name]
    formData.append("data", JSON.stringify({
      cc: values.cc.join(", "),
      bcc: values.bcc.join(", "),
      subject: values.subject,
      emailBody: emailBodyHtml,
    }));

    // Add attachments
    if (attachments.length > 0) {
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });
      formData.append("attachmentCount", attachments.length.toString());
    } else {
      formData.append("attachmentCount", "0");
    }


    setLoading(true);

    try {
      const response = await apiClient("/Email/SendClientPortalUpdate", {
        method: "POST",
        headers: new Headers({
          Authorization: `Bearer ${token}`,
        }),
        body: formData
      });

      if (response?.status === true || response?.resp === 0) {
        Swal.fire({
          title: "Success",
          text: response?.message,
          icon: "success",
          confirmButtonColor: '#02A4E3',
        });
        
        // Reset form
        resetForm();
        setAttachments([]);
        setCcInput("");
        setBccInput("");
        const contentBlock = convertFromHTML(emailTemplate);
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const _editorState = EditorState.createWithContent(contentState);
        setEditorState(_editorState);
      } else {
        Swal.fire({
          title: "Error",
          text: response?.message || "Failed to send email",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      Swal.fire({
        title: "Error",
        text: "An error occurred while sending the email",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="card-title">
            <span className="d-flex"> Client Portal Update Email</span>
        </div>
      </div>
      <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>
      <Box sx={{ width: "100%" }}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              <Grid container spacing={3}>
                {/* CC Field */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="CC"
                    name="cc"
                    value={ccInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.includes(",") || val.includes(";") || val.includes(" ")) {
                        handleAddEmail(val, "cc", values.cc, setFieldValue, setCcInput);
                      } else {
                        setCcInput(val);
                      }
                    }}
                    onKeyDown={(e) =>
                      handleEmailKeyDown(
                        e as React.KeyboardEvent<HTMLInputElement>,
                        ccInput,
                        "cc",
                        values.cc,
                        setFieldValue,
                        setCcInput
                      )
                    }
                    onBlur={() => {
                      if (ccInput.trim()) {
                        handleAddEmail(ccInput, "cc", values.cc, setFieldValue, setCcInput);
                      }
                    }}
                    placeholder={values.cc.length === 0 ? "Enter CC email addresses" : ""}
                    variant="outlined"
                    InputProps={{
                      startAdornment: values.cc.length > 0 && (
                        values.cc.map((email, index) => (
                          <Chip
                            key={index}
                            label={email}
                            size="small"
                            onDelete={() => handleRemoveEmail(index, "cc", values.cc, setFieldValue)}
                          />
                        ))
                      ),
                      sx: { 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        alignItems: 'center',
                        gap: 0.5,
                        py: 1.5,
                        '& input': { 
                          flex: '1 0 50px', 
                          width: 'auto',
                          minHeight: '24px',
                          height: '24px',
                          padding: '0px 10px !important'
                        }
                      }
                    }}
                  />
                </Grid>

                {/* BCC Field */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="BCC"
                    name="bcc"
                    value={bccInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.includes(",") || val.includes(";") || val.includes(" ")) {
                        handleAddEmail(val, "bcc", values.bcc, setFieldValue, setBccInput);
                      } else {
                        setBccInput(val);
                      }
                    }}
                    onKeyDown={(e) =>
                      handleEmailKeyDown(
                        e as React.KeyboardEvent<HTMLInputElement>,
                        bccInput,
                        "bcc",
                        values.bcc,
                        setFieldValue,
                        setBccInput
                      )
                    }
                    onBlur={() => {
                      if (bccInput.trim()) {
                        handleAddEmail(bccInput, "bcc", values.bcc, setFieldValue, setBccInput);
                      }
                    }}
                    placeholder={values.bcc.length === 0 ? "Enter BCC email addresses" : ""}
                    variant="outlined"
                    InputProps={{
                      startAdornment: values.bcc.length > 0 && (
                        values.bcc.map((email, index) => (
                          <Chip
                            key={index}
                            label={email}
                            size="small"
                            onDelete={() => handleRemoveEmail(index, "bcc", values.bcc, setFieldValue)}
                          />
                        ))
                      ),
                      sx: { 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        alignItems: 'center',
                        gap: 0.5,
                        py: 1.5,
                        '& input': { 
                          flex: '1 0 50px', 
                          width: 'auto',
                          minHeight: '24px',
                          height: '24px',
                          padding: '0px 10px !important'
                        }
                      }
                    }}
                  />
                </Grid>

                {/* Subject Field */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={values.subject}
                    onChange={(e) => setFieldValue("subject", e.target.value)}
                    variant="outlined"
                    required
                  />
                </Grid>

                {/* Email Body Editor */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Email Body
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1, minHeight: 400 }}>
                    <div ref={editorWrapperRef}>
                      <Editor
                        editorState={editorState}
                        onEditorStateChange={setEditorState}
                        toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'emoji', 'image', 'remove', 'history'],
                        image: {
                          uploadCallback: (file: File) => {
                            return new Promise((resolve, reject) => {
                              const reader = new FileReader();
                              reader.onload = () => {
                                resolve({ data: { link: reader.result } });
                              };
                              reader.onerror = reject;
                              reader.readAsDataURL(file);
                            });
                          },
                          alt: { present: true, mandatory: false }
                        }
                      }}
                        editorStyle={{
                          minHeight: 300,
                          padding: '10px',
                          border: '1px solid #ddd'
                        }}
                      />
                    </div>
                  </Paper>
                </Grid>

                {/* Attachment Upload */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Attachments
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFileIcon />}
                    >
                      Add Attachment
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={handleFileChange}
                      />
                    </Button>
                  </Box>
                  
                  {attachments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      {attachments.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          onDelete={() => handleRemoveAttachment(index)}
                          deleteIcon={<DeleteIcon />}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2, mb: 4 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting || loading}
                      sx={{ minWidth: 150 }}
                    >
                      {isSubmitting || loading ? "Sending..." : "Send Email"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};
